/**
 * Centralized glucose unit conversion utilities
 * SINGLE SOURCE OF TRUTH for glucose unit conversions
 *
 * All glucose unit conversions across the application MUST use these functions.
 * DO NOT duplicate conversion logic elsewhere.
 */

/**
 * Conversion factor from mmol/L to mg/dL
 * 1 mmol/L = 18.0182 mg/dL (exact molecular weight-based conversion)
 */
export const MMOL_TO_MGDL_FACTOR = 18.0182

/**
 * Glucose unit types
 */
export type GlucoseUnit = 'mmol/L' | 'mg/dL'

/**
 * Convert glucose value from mmol/L to mg/dL
 * @param mmol - Glucose value in mmol/L
 * @returns Glucose value in mg/dL (rounded to nearest integer)
 */
export function mmolToMgdl(mmol: number): number {
  return Math.round(mmol * MMOL_TO_MGDL_FACTOR)
}

/**
 * Convert glucose value from mg/dL to mmol/L
 * @param mgdl - Glucose value in mg/dL
 * @returns Glucose value in mmol/L
 */
export function mgdlToMmol(mgdl: number): number {
  return mgdl / MMOL_TO_MGDL_FACTOR
}

/**
 * Convert glucose value based on target unit
 * @param mmolValue - Glucose value in mmol/L (source unit)
 * @param targetUnit - Target unit ('mmol/L' or 'mg/dL')
 * @returns Converted glucose value
 */
export function convertGlucose(mmolValue: number, targetUnit: GlucoseUnit): number {
  return targetUnit === 'mg/dL' ? mmolToMgdl(mmolValue) : mmolValue
}

/**
 * Format glucose value for display with appropriate precision
 * @param mmolValue - Glucose value in mmol/L
 * @param unit - Display unit ('mmol/L' or 'mg/dL')
 * @returns Formatted string with appropriate decimal places
 */
export function formatGlucoseValue(mmolValue: number, unit: GlucoseUnit): string {
  const value = convertGlucose(mmolValue, unit)
  // mg/dL: no decimals, mmol/L: 1 decimal place
  return unit === 'mg/dL' ? value.toFixed(0) : value.toFixed(1)
}

/**
 * Format glucose value with unit suffix — WITHOUT the out-of-range mask.
 *
 * ⚠️ Do NOT use this for a glucose READING shown to a vet: a clamped sensor
 * drop-out (exactly 2.2 mmol/L) prints as a confident "2.2 mmol/L" here,
 * while every chart masks it as "<2.2". Readings go through
 * `formatGlucoseForDisplay` (src/common.ts). This stays only for values
 * where masking would be WRONG — e.g. `localizeGlucoseInMessage` below
 * rewrites free text containing vet-configured thresholds, and a threshold
 * of 2.0 must not become "<2.2".
 *
 * @param mmolValue - Glucose value in mmol/L
 * @param unit - Display unit ('mmol/L' or 'mg/dL')
 * @returns Formatted string with unit (e.g., "5.5 mmol/L" or "99 mg/dL")
 */
export function formatGlucoseWithUnit(mmolValue: number, unit: GlucoseUnit): string {
  return `${formatGlucoseValue(mmolValue, unit)} ${unit}`
}

/**
 * Convert a threshold value based on unit
 * Useful for converting glucose thresholds for display
 * @param mmolThreshold - Threshold in mmol/L
 * @param unit - Target unit
 * @returns Converted threshold value
 */
export function convertThreshold(mmolThreshold: number, unit: GlucoseUnit): number {
  return convertGlucose(mmolThreshold, unit)
}

/**
 * Substitute every "X.X mmol/L" token in a backend-generated string with
 * the user's preferred unit. The Go alert-engine and notification system
 * hardcode "mmol/L" in their message templates (see
 * `go-services/internal/alert/patterns.go` and `processor.go`); this is
 * the display-side equalizer so the message body matches the unit-aware
 * badge that `formatGlucoseWithUnit` produces.
 *
 * No-op when `unit === 'mmol/L'`.
 *
 * Caveat: pure regex on `\d+(\.\d+)?\s*mmol\/L`. Assumes every "mmol/L"
 * mention in alert messages is preceded by a numeric value. Holds for
 * every current template; if the engine adds free-text references to
 * "mmol/L" (e.g. units-of-measure documentation), this will mis-rewrite
 * those too.
 */
export function localizeGlucoseInMessage(message: string, unit: GlucoseUnit): string {
  if (!message || unit === 'mmol/L') return message
  return message.replace(/(-?\d+(?:\.\d+)?)\s*mmol\/L/g, (_match, num: string) => {
    const mmol = parseFloat(num)
    if (!Number.isFinite(mmol)) return _match
    return formatGlucoseWithUnit(mmol, unit)
  })
}

/**
 * Get Y-axis maximum for charts based on glucose unit
 * @param maxGlucose - Maximum glucose value in mmol/L
 * @param unit - Display unit
 * @returns Appropriate Y-axis maximum with padding
 */
export function getChartYAxisMax(maxGlucose: number, unit: GlucoseUnit): number {
  if (unit === 'mg/dL') {
    return Math.max(360, mmolToMgdl(maxGlucose) + 36)
  }
  return Math.max(20, maxGlucose + 2)
}
